// Typed API client. `paths` is generated from openapi.json, which `ocs-api openapi`
// prints (npm run generate:api); openapi-fetch turns it into a client whose request and
// response types follow the spec, so nothing here is hand-maintained (docs/09 "Web UI").
import createClient, { type Middleware } from "openapi-fetch";

import type { components, paths } from "./schema";

// The spec's paths carry their `/api` prefix, so the base is the page origin; in
// development Vite proxies /api to ocs-api (vite.config.ts). openapi-fetch builds a
// Request, which needs an absolute URL outside a document context (tests). fetch is
// looked up per call rather than captured at import, so tests can replace it.
const baseUrl = typeof location !== "undefined" ? location.origin : "http://ocs.invalid";

export const api = createClient<paths>({
  baseUrl,
  fetch: (request) => globalThis.fetch(request),
});

/**
 * Device-session authentication for the conversation endpoints (docs/07): the bearer
 * token from `/api/devices/{id}/session`, attached while a session is open. The relay
 * `hello` carries the same token.
 */
let sessionToken: string | undefined;

export function setSessionToken(token: string | undefined): void {
  sessionToken = token;
}

const bearer: Middleware = {
  onRequest({ request }) {
    if (sessionToken) request.headers.set("Authorization", `Bearer ${sessionToken}`);
    return request;
  },
};
api.use(bearer);

/**
 * The signed-in session (D48) is an HttpOnly cookie the browser sends by itself; script
 * never sees it. Every state-changing request must carry `X-OCS-CSRF: 1`, which a page on
 * another origin cannot add without a CORS preflight the API never grants. When the API
 * refuses for want of a session (`x-ocs-auth: sign-in`) or a password change
 * (`x-ocs-auth: change-password`), the listeners (the route guard) send the user there.
 */
export type AuthHint = "sign-in" | "change-password";

const authListeners = new Set<(hint: AuthHint) => void>();

export function onAuthHint(listener: (hint: AuthHint) => void): () => void {
  authListeners.add(listener);
  return () => {
    authListeners.delete(listener);
  };
}

/** Tell the listeners when a response asks for a sign-in or a password change. */
export function reportAuthHint(response: Response): void {
  const hint = response.headers.get("x-ocs-auth");
  if (hint === "sign-in" || hint === "change-password") authListeners.forEach((l) => l(hint));
}

const session: Middleware = {
  onRequest({ request }) {
    request.headers.set("X-OCS-CSRF", "1");
    return request;
  },
  onResponse({ response }) {
    reportAuthHint(response);
    return response;
  },
};
api.use(session);

export type Schemas = components["schemas"];
export type Organisation = Schemas["OrgDto"];
export type Environment = Schemas["EnvironmentDto"];
export type Workspace = Schemas["WorkspaceDto"];
export type Workflow = Schemas["WorkflowDto"];
export type Run = Schemas["RunDto"];
export type Question = Schemas["QuestionDto"];
export type Conversation = Schemas["ConversationDto"];
export type ConversationDetail = Schemas["ConversationDetailDto"];
export type Segment = Schemas["SegmentDto"];
export type Device = Schemas["DeviceDto"];
export type Health = Schemas["Health"];
export type User = Schemas["UserDto"];
